import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MdArrowBack, MdPerson, MdEmail, MdPhone, MdBadge, MdEdit } from 'react-icons/md';
import { UPDATE_LOGGED_IN_PROFILE_API } from '../config/API';
import { useUser } from '../context/UserContext';
import Loader from '../components/common/Loader';
import { authFetch } from '../components/utils/authFetch';
import './Profile.css';

const Profile = () => {
    const navigate = useNavigate();
    const { userProfile } = useUser();
    const [isEditing, setIsEditing] = useState(false);

    // Initialize with safe defaults to avoid uncontrolled inputs warning
    const [editForm, setEditForm] = useState({
        firstName: '',
        lastName: '',
        email: '',
        contactNumber: '',
        userName: '',
        gender: '',
        roleId: ''
    });

    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [updating, setUpdating] = useState(false);

    useEffect(() => {
        const fetchUserProfile = async () => {
            try {
                // Get the user data directly from local storage
                const storedUser = localStorage.getItem('user');

                if (storedUser) {
                    const currentUser = JSON.parse(storedUser);
                    setUserData(currentUser);
                    // Ensure editForm has valid values (no undefined)
                    setEditForm({
                        ...currentUser,
                        firstName: currentUser.firstName || '',
                        lastName: currentUser.lastName || '',
                        email: currentUser.email || '',
                        contactNumber: currentUser.contactNumber || '',
                        userName: currentUser.username || '',
                        gender: currentUser.gender || '',
                        roleId: currentUser.roleId || ''
                    });
                } else {
                    setError('User profile details not found in local storage.');
                }
            } catch (err) {
                console.error('Error fetching profile from local storage:', err);
                setError('An error occurred while loading profile.');
            } finally {
                setLoading(false);
            }
        };

        if (userProfile) {
            fetchUserProfile();
        }
    }, [userProfile]);

    const handleEditClick = () => {
        setEditForm(userData);
        setIsEditing(true);
    };

    const handleCancelClick = () => {
        setIsEditing(false);
        setEditForm(userData);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEditForm(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSave = async () => {
        setUpdating(true);
        try {
            const token = localStorage.getItem('accessToken');
            const payload = {
                firstName: editForm.firstName || '',
                lastName: editForm.lastName || '',
                contactNumber: editForm.contactNumber || '',
                gender: editForm.gender || '',
                email: editForm.email || '',
                username: userData.username || '',
                // Not sending password fields to avoid changing them
            };

            const response = await axios.put(
                UPDATE_LOGGED_IN_PROFILE_API,
                payload,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.status || response.data.message === 'Employee updated successfully') {
                const updatedUser = { ...userData, ...payload };
                setUserData(updatedUser);
                localStorage.setItem('user', JSON.stringify(updatedUser)); // Update LocalStorage
                setIsEditing(false);
                // Swal.fire('Success', 'Profile updated successfully!', 'success');
            } else {
                // Swal.fire('Error', response.data.message || 'Failed to update profile', 'error');
                alert(response.data.message || 'Failed to update profile');
            }

        } catch (err) {
            console.error('Error updating profile:', err);
            const errorMessage = err.response?.data?.message || err.message || 'An error occurred while updating profile.';
            // Swal.fire('Error', errorMessage, 'error');
            alert(errorMessage);
        } finally {
            setUpdating(false);
        }
    };

    if (loading) return <Loader text="Loading Profile..." />;

    if (error) {
        return (
            <div className="profile-page-error">
                <div className="error-card">
                    <h3>Error</h3>
                    <p>{error}</p>
                    <button onClick={() => navigate('/')} className="back-btn-simple">Go to Dashboard</button>
                </div>
            </div>
        );
    }

    return (
        <div className="profile-page">
            <div className="page-header">
                <button onClick={() => navigate(-1)} className="back-btn">
                    <MdArrowBack />
                </button>
                <h2>My Profile</h2>
            </div>

            <div className="profile-container">
                <div className="profile-card">
                    <div className="profile-header-section">
                        <div className="profile-avatar-large">
                            {userData?.firstName?.[0]?.toUpperCase()}{userData?.lastName?.[0]?.toUpperCase()}
                        </div>
                        {isEditing ? (
                            <div className="edit-name-inputs">
                                <input
                                    type="text"
                                    name="firstName"
                                    value={editForm.firstName}
                                    onChange={handleInputChange}
                                    className="profile-input-name"
                                    placeholder="First Name"
                                />
                                <input
                                    type="text"
                                    name="lastName"
                                    value={editForm.lastName}
                                    onChange={handleInputChange}
                                    className="profile-input-name"
                                    placeholder="Last Name"
                                />
                            </div>
                        ) : (
                            <h3 className="profile-name">{userData?.firstName} {userData?.lastName}</h3>
                        )}
                        {/* <span className="profile-role-badge">{userData?.roleName || userData?.role || 'User'}</span> */}
                    </div>

                    <div className="profile-details-grid">
                        <div className="detail-item">
                            <div className="detail-icon">
                                <MdPerson />
                            </div>
                            <div className="detail-content">
                                <label>Username</label>
                                <p>{userData?.username}</p>
                                {/* Usually Update of Username is restricted or handled carefully */}
                            </div>
                        </div>

                        <div className="detail-item">
                            <div className="detail-icon">
                                <MdEmail />
                            </div>
                            <div className="detail-content">
                                <label>Email Address</label>
                                {isEditing ? (
                                    <input
                                        type="email"
                                        name="email"
                                        value={editForm.email}
                                        onChange={handleInputChange}
                                        className="profile-input"
                                    />
                                ) : (
                                    <p>{userData?.email}</p>
                                )}
                            </div>
                        </div>

                        <div className="detail-item">
                            <div className="detail-icon">
                                <MdPhone />
                            </div>
                            <div className="detail-content">
                                <label>Contact Number</label>
                                {isEditing ? (
                                    <input
                                        type="text"
                                        name="contactNumber"
                                        value={editForm.contactNumber}
                                        onChange={handleInputChange}
                                        className="profile-input"
                                        placeholder="Phone Number"
                                    />
                                ) : (
                                    <p>{userData?.contactNumber}</p>
                                )}
                            </div>
                        </div>

                        {/* <div className="detail-item">
                            <div className="detail-icon">
                                <MdBadge />
                            </div>
                            <div className="detail-content">
                                <label>Employee ID</label>
                                <p>EMP-{userData?.id}</p>
                            </div>
                        </div> */}

                        {userData?.gender && (
                            <div className="detail-item">
                                <div className="detail-icon">
                                    <MdPerson />
                                </div>
                                <div className="detail-content">
                                    <label>Gender</label>
                                    {isEditing ? (
                                        <select
                                            name="gender"
                                            value={editForm.gender}
                                            onChange={handleInputChange}
                                            className="profile-input"
                                        >
                                            <option value="Male">Male</option>
                                            <option value="Female">Female</option>
                                        </select>
                                    ) : (
                                        <p>{userData.gender}</p>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="profile-actions">
                        {isEditing ? (
                            <>
                                <button className="cancel-edit-btn" onClick={handleCancelClick} disabled={updating}>
                                    Cancel
                                </button>
                                <button className="save-edit-btn" onClick={handleSave} disabled={updating}>
                                    {updating ? 'Saving...' : 'Save Changes'}
                                </button>
                            </>
                        ) : (
                            <>
                                <button className="edit-profile-btn" onClick={() => navigate('/reset-password')} style={{ marginRight: '1rem' }}>
                                    Reset Password
                                </button>
                                <button className="edit-profile-btn-primary" onClick={handleEditClick}>
                                    <MdEdit /> Edit Profile
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Profile;
